import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Bot, Sparkles, Zap, TrendingUp } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
const AI_ALLY_VIDEO_URL = "/videos/ai-ally-video.mp4";
import logoOpenAI from "@/assets/logo-openai.png";
import logoGemini from "@/assets/logo-gemini.webp";
import logoClaude from "@/assets/logo-claude.webp";
import logoMetaAI from "@/assets/logo-meta-ai.webp";
import logoN8n from "@/assets/logo-n8n-ai.png";

const aiLogos = [
  { name: "OpenAI", src: logoOpenAI },
  { name: "Gemini", src: logoGemini },
  { name: "Claude", src: logoClaude },
  { name: "Meta AI", src: logoMetaAI },
  { name: "n8n", src: logoN8n },
];

const capKeys = [
  { icon: Sparkles, titleKey: "ai.cap1.title", descKey: "ai.cap1.desc" },
  { icon: Zap, titleKey: "ai.cap2.title", descKey: "ai.cap2.desc" },
  { icon: TrendingUp, titleKey: "ai.cap3.title", descKey: "ai.cap3.desc" },
  { icon: Bot, titleKey: "ai.cap4.title", descKey: "ai.cap4.desc" },
];

const AIAllySection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [150, 0, 0, -80]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.1, 0.9, 1], [0, 1, 1, 0.3]);
  const contentScale = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0.9, 1, 1, 0.93]);

  const titleWords = (t("ai.title1") || "").split(" ");

  return (
    <section id="ia" className="py-24 md:py-32 px-6 bg-background text-foreground overflow-hidden" ref={ref}>
      <motion.div className="max-w-7xl mx-auto" style={{ y: contentY, opacity: contentOpacity, scale: contentScale }}>
        <motion.div className="mb-12 md:mb-16">
          <motion.p
            className="text-xs uppercase tracking-[0.25em] font-bold text-brand-amarillo mb-4 bg-primary-foreground text-sidebar-foreground"
            initial={{ opacity: 0, x: -30, filter: "blur(6px)" }}
            animate={inView ? { opacity: 1, x: 0, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t("ai.tag")}
          </motion.p>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tight lowercase leading-[0.95]">
            {titleWords.map((word, i) => (
              <span key={i} className="inline-block overflow-hidden mr-[0.25em]">
                <motion.span
                  className="inline-block"
                  initial={{ y: "120%", rotateX: 40 }}
                  animate={inView ? { y: "0%", rotateX: 0 } : {}}
                  transition={{ duration: 0.7, delay: 0.15 + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                >
                  {word}
                </motion.span>
              </span>
            ))}
            <br />
            <span className="inline-block overflow-hidden">
              <motion.span
                className="inline-block"
                initial={{ y: "120%", rotateX: 40 }}
                animate={inView ? { y: "0%", rotateX: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.15 + titleWords.length * 0.07, ease: [0.22, 1, 0.36, 1] }}
              >
                {t("ai.title2")}
              </motion.span>
            </span>{" "}
            <span className="inline-block overflow-hidden">
              <motion.span
                className="inline-block text-brand-rosa"
                initial={{ y: "120%", rotateX: 40, scale: 0.8 }}
                animate={inView ? { y: "0%", rotateX: 0, scale: 1 } : {}}
                transition={{ duration: 0.7, delay: 0.15 + (titleWords.length + 1) * 0.07, type: "spring", stiffness: 150 }}
              >
                IA
              </motion.span>
            </span>
            <motion.span
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.6 }}
            >.</motion.span>
            <br />
            <span className="inline-block overflow-hidden">
              <motion.span
                className="inline-block text-foreground/30 my-0 py-[9px]"
                initial={{ y: "120%" }}
                animate={inView ? { y: "0%" } : {}}
                transition={{ duration: 0.7, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                {t("ai.title3")}
              </motion.span>
            </span>
          </h2>
        </motion.div>

        <motion.div
          className="relative w-full rounded-2xl overflow-hidden mb-16 md:mb-20"
          initial={{ opacity: 0, y: 60, scale: 0.95, filter: "blur(10px)" }}
          animate={inView ? { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" } : {}}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="aspect-[21/9] md:aspect-[21/8] relative">
            <video
              src={AI_ALLY_VIDEO_URL}
              autoPlay
              loop
              muted
              playsInline
              preload="none"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <motion.p
              className="absolute bottom-6 md:bottom-10 left-6 md:left-10 text-white text-lg md:text-2xl font-bold max-w-lg leading-snug"
              initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
              animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              {t("ai.video.text")}
            </motion.p>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {capKeys.map((cap, i) => (
            <motion.div
              key={cap.titleKey}
              className="group p-6 rounded-2xl border border-foreground/[0.06] hover:border-foreground/20 transition-all duration-300"
              initial={{ opacity: 0, y: 50, scale: 0.9, filter: "blur(8px)" }}
              animate={inView ? { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" } : {}}
              transition={{ duration: 0.6, delay: 0.5 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6, borderColor: "rgba(236,72,153,0.3)" }}
            >
              <motion.div
                className="w-10 h-10 rounded-xl bg-brand-rosa/10 flex items-center justify-center mb-4 group-hover:bg-brand-rosa/20 transition-colors text-primary"
                initial={{ scale: 0, rotate: -60 }}
                animate={inView ? { scale: 1, rotate: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.6 + i * 0.12, type: "spring", stiffness: 200 }}
              >
                <cap.icon className="w-5 h-5 text-brand-rosa" />
              </motion.div>
              <motion.h3
                className="text-sm font-black uppercase tracking-wide mb-2"
                initial={{ opacity: 0, x: -15 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.65 + i * 0.12 }}
              >
                {t(cap.titleKey)}
              </motion.h3>
              <motion.p
                className="text-sm text-foreground/40 leading-relaxed"
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : {}}
                transition={{ duration: 0.4, delay: 0.7 + i * 0.12 }}
              >
                {t(cap.descKey)}
              </motion.p>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 grid md:grid-cols-[1fr_auto] gap-10 items-center">
          <motion.p
            className="text-lg md:text-xl text-foreground/50 max-w-2xl leading-relaxed"
            initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
            animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.8, delay: 1.0 }}
          >
            {t("ai.bottom")} <span className="text-foreground font-semibold">{t("ai.bottom.tech")}</span> {t("ai.bottom.and")} <span className="text-foreground font-semibold">{t("ai.bottom.human")}</span>.
          </motion.p>

          <motion.div
            className="flex flex-wrap items-center justify-center gap-[18px]"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 1.1 }}
          >
            {aiLogos.map((logo, i) => (
              <motion.img
                key={logo.name}
                src={logo.src}
                alt={logo.name}
                loading="lazy"
                decoding="async"
                className="h-8 md:h-10 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity duration-300"
                initial={{ opacity: 0, scale: 0, rotate: -15 }}
                animate={inView ? { opacity: 0.8, scale: 1, rotate: 0 } : {}}
                transition={{ type: "spring", stiffness: 200, damping: 15, delay: 1.1 + i * 0.1 }}
                whileHover={{ scale: 1.15, rotate: 5 }}
              />
            ))}
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default AIAllySection;
